import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AuthError, Session, User } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { identifyUser, resetUser } from './analytics'

// ON-407: one Supabase auth session for the whole site, shared by the captain
// portal and the public event page. Wraps the auth calls both surfaces need;
// each surface decides its own OAuth redirect (captain strips the query,
// the event page keeps rsvp/ref/src) and maps errors to its own copy.
//
// Analytics: identify with the Supabase user id on session restore and on
// every sign-in; reset on sign-out.

type OAuthProvider = 'google' | 'apple'

export interface SessionApi {
  session: Session | null
  user: User | null
  loading: boolean
  signInWithPassword: (email: string, password: string) => Promise<AuthError | null>
  /** Navigates away on success; resolves only with an error when the redirect could not start. */
  signInWithOAuth: (
    provider: OAuthProvider,
    redirectTo: string,
    queryParams?: Record<string, string>,
  ) => Promise<AuthError | null>
  signUp: (
    email: string,
    password: string,
    metadata: Record<string, unknown>,
    emailRedirectTo: string,
  ) => Promise<{ user: User | null; error: AuthError | null }>
  resendSignup: (email: string, emailRedirectTo: string) => Promise<AuthError | null>
  resetPassword: (email: string, redirectTo: string) => Promise<AuthError | null>
  updateMetadata: (data: Record<string, unknown>) => Promise<AuthError | null>
  /**
   * Signs out of THIS browser only. Supabase's default scope ('global') revokes
   * every refresh token for the user — including the mobile app's.
   */
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionApi | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setLoading(false)
      if (data.session?.user) identifyUser(data.session.user.id)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next)
      if (event === 'SIGNED_OUT') resetUser()
      else if (next?.user) identifyUser(next.user.id)
    })
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    return error
  }, [])

  const signInWithOAuth = useCallback(
    async (provider: OAuthProvider, redirectTo: string, queryParams?: Record<string, string>) => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: queryParams ? { redirectTo, queryParams } : { redirectTo },
      })
      return error
    },
    [],
  )

  const signUp = useCallback(
    async (email: string, password: string, metadata: Record<string, unknown>, emailRedirectTo: string) => {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: metadata, emailRedirectTo },
      })
      return { user: data?.user ?? null, error }
    },
    [],
  )

  const resendSignup = useCallback(async (email: string, emailRedirectTo: string) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email: email.trim(), options: { emailRedirectTo } })
    return error
  }, [])

  const resetPassword = useCallback(async (email: string, redirectTo: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo })
    return error
  }, [])

  const updateMetadata = useCallback(async (data: Record<string, unknown>) => {
    const { data: out, error } = await supabase.auth.updateUser({ data })
    if (!error && out?.user) {
      const user = out.user
      setSession((prev) => (prev ? { ...prev, user } : prev))
    }
    return error
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'local' })
    resetUser()
  }, [])

  const value = useMemo<SessionApi>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signInWithPassword,
      signInWithOAuth,
      signUp,
      resendSignup,
      resetPassword,
      updateMetadata,
      signOut,
    }),
    [session, loading, signInWithPassword, signInWithOAuth, signUp, resendSignup, resetPassword, updateMetadata, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <AuthProvider>')
  return ctx
}
